import clsx from 'clsx';
import { Stack } from 'components';
import AccordionComponent from 'components/accordion/accordion';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import { useState } from 'react';
import { useResponsive } from 'utils/hooks/useMediaQuery';

import style from './careGaps.module.scss';

type CareGapItem = {
  id?: string | null;
  category?: string | null;
  lastUpdated?: string | null;
};

type CareGapsFormValues = {
  careGaps?: CareGapItem[] | null;
};

type SortKey = 'category' | 'lastUpdated' | null;
type SortDirection = 'asc' | 'desc';

const getSortValue = (item: CareGapItem, key: Exclude<SortKey, null>) => {
  if (key === 'category') {
    return item.category?.trim().toLowerCase() ?? null;
  }

  if (!item.lastUpdated) {
    return null;
  }

  const parsed = dayjs(item.lastUpdated);

  return parsed.isValid() ? parsed.valueOf() : null;
};

const compareNullableValues = (
  a: string | number | null,
  b: string | number | null,
  direction: SortDirection
) => {
  if (a == null && b == null) {
    return 0;
  }

  if (a == null) {
    return 1;
  }

  if (b == null) {
    return -1;
  }

  const base =
    typeof a === 'string' && typeof b === 'string' ? a.localeCompare(b) : Number(a) - Number(b);

  return direction === 'asc' ? base : base * -1;
};

const formatLastUpdated = (value?: string | null) => {
  if (!value) {
    return '--';
  }

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed.format('MM/DD/YY h:mm a') : '--';
};

const CareGaps = () => {
  const { values } = useFormikContext<CareGapsFormValues>();
  const { isTablet } = useResponsive();
  const [sortKey, setSortKey] = useState<SortKey>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const careGaps = values.careGaps ?? [];
  const gridTemplateColumns = isTablet ? 'minmax(0, 1fr) 168px' : 'minmax(0, 1fr) 258px';

  const handleSort = (key: Exclude<SortKey, null>) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection('asc');
      return;
    }

    if (sortDirection === 'asc') {
      setSortDirection('desc');
      return;
    }

    setSortKey(null);
    setSortDirection('asc');
  };

  const sortedCareGaps = careGaps
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      if (sortKey == null) {
        return a.index - b.index;
      }

      const compared = compareNullableValues(
        getSortValue(a.item, sortKey),
        getSortValue(b.item, sortKey),
        sortDirection
      );

      return compared === 0 ? a.index - b.index : compared;
    })
    .map(({ item }) => item);

  const getSortIcon = (key: Exclude<SortKey, null>) => {
    if (sortKey !== key) {
      return 'unfold_more';
    }

    return sortDirection === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
  };

  return (
    <AccordionComponent accordionTabId="careGaps" title="Care Gaps">
      <Stack width="100%" pt="24px" pb="24px" data-test-id="profile-care-gaps">
        <div className={style.table}>
          <div className={style.headerRow} style={{ gridTemplateColumns }}>
            <button
              type="button"
              className={style.headerButton}
              onClick={() => handleSort('category')}
              aria-label={`Sort by category ${sortKey === 'category' ? sortDirection : ''}`.trim()}
            >
              <span>Category</span>
              <span className={clsx('material-icons', style.sortIcon)}>
                {getSortIcon('category')}
              </span>
            </button>
            <button
              type="button"
              className={style.headerButton}
              onClick={() => handleSort('lastUpdated')}
              aria-label={`Sort by last updated ${
                sortKey === 'lastUpdated' ? sortDirection : ''
              }`.trim()}
            >
              <span>Last Updated</span>
              <span className={clsx('material-icons', style.sortIcon)}>
                {getSortIcon('lastUpdated')}
              </span>
            </button>
          </div>

          {sortedCareGaps.length === 0 && <div className={style.emptyRow}></div>}

          {sortedCareGaps.map((careGap, index) => (
            <div
              className={style.dataRow}
              style={{ gridTemplateColumns }}
              key={careGap.id ?? `${careGap.category ?? 'care-gap'}-${index}`}
            >
              <div className={style.cell}>{careGap.category?.trim() || '--'}</div>
              <div className={style.cell}>{formatLastUpdated(careGap.lastUpdated)}</div>
            </div>
          ))}
        </div>
      </Stack>
    </AccordionComponent>
  );
};

export default CareGaps;
